/**
 * Cookies composable - Full browser cookies API wrapper
 *
 * This composable exposes a complete cookies API and can be used
 * anywhere in the application, not just with the persist plugin.
 *
 * For persist plugin integration, use CookiesAdapter.
 *
 * Note: Cookies are limited to 4KB per cookie and have domain/path restrictions.
 */
export function useCookies () {
	/**
     * Parse a cookie string into key-value pairs
     * @param {string} cookieString - The cookie string to parse
     * @returns {Object} Object with cookie key-value pairs
     */
	function parseCookies (cookieString) {
		const cookies = {};
		if (!cookieString) return cookies;

		cookieString.split(';').forEach(cookie => {
			const [name, ...rest] = cookie.split('=');
			const value = rest.join('=').trim();
			if (name) {
				cookies[name.trim()] = decodeURIComponent(value);
			}
		});

		return cookies;
	}

	/**
     * Get a cookie value by key
     * @param {string} key - The cookie name to retrieve
     * @returns {Promise<string|null>} The cookie value or null if not found
     */
	async function getItem (key) {
		const cookies = parseCookies(document.cookie);
		return cookies[key] || null;
	}

	/**
     * Set a cookie
     * @param {string} key - The cookie name
     * @param {string} value - The value to store
     * @param {Object} options - Cookie options
     * @param {number} options.maxAge - Max age in seconds
     * @param {Date} options.expires - Expiration date
     * @param {string} options.path - Cookie path (default: '/')
     * @param {string} options.domain - Cookie domain
     * @param {boolean} options.secure - Secure flag (HTTPS only)
     * @param {string} options.sameSite - SameSite attribute ('Strict', 'Lax', 'None')
     * @returns {Promise<void>}
     */
	async function setItem (key, value, options = {}) {
		const {
			maxAge,
			expires,
			path = '/',
			domain,
			secure,
			sameSite = 'Lax',
		} = options;

		let cookieString = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;

		if (maxAge !== undefined) {
			cookieString += `; max-age=${maxAge}`;
		}

		if (expires) {
			cookieString += `; expires=${expires.toUTCString()}`;
		}

		if (path) {
			cookieString += `; path=${path}`;
		}

		if (domain) {
			cookieString += `; domain=${domain}`;
		}

		if (secure) {
			cookieString += '; secure';
		}

		if (sameSite) {
			cookieString += `; samesite=${sameSite}`;
		}

		document.cookie = cookieString;
	}

	/**
     * Remove a cookie
     * @param {string} key - The cookie name to remove
     * @param {Object} options - Cookie options (path, domain)
     * @returns {Promise<void>}
     */
	async function removeItem (key, options = {}) {
		await setItem(key, '', {
			...options,
			maxAge: -1,
		});
	}

	/**
     * Clear all cookies
     * Note: This only clears cookies accessible to the current path/domain
     * @returns {Promise<void>}
     */
	async function clear () {
		const cookies = parseCookies(document.cookie);
		const keys = Object.keys(cookies);

		for (const key of keys) {
			await removeItem(key);
		}
	}

	/**
     * Get all cookie names
     * @returns {Promise<string[]>} Array of cookie names
     */
	async function keys () {
		const cookies = parseCookies(document.cookie);
		return Object.keys(cookies);
	}

	/**
     * Get all cookies as key-value pairs
     * @returns {Promise<Object>} Object with all cookies
     */
	async function getAll () {
		return parseCookies(document.cookie);
	}

	/**
     * Check if a cookie exists
     * @param {string} key - The cookie name to check
     * @returns {Promise<boolean>} True if exists
     */
	async function has (key) {
		const value = await getItem(key);
		return value !== null;
	}

	/**
     * Get the number of cookies
     * @returns {Promise<number>} Number of cookies
     */
	async function getLength () {
		const cookieKeys = await keys();
		return cookieKeys.length;
	}

	return {
		// Core cookie API
		getItem,
		setItem,
		removeItem,
		clear,

		// Convenience methods
		keys,
		getAll,
		has,
		getLength,
	};
}
